
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export function ForgotPasswordForm({ onBack }: ForgotPasswordFormProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const navigate = useNavigate();

  // Handle countdown timer if there's a cooldown
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setCooldownRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (cooldownRemaining > 0) {
      toast.error(t('reset_rate_limit_message').replace('{{seconds}}', String(cooldownRemaining)));
      return;
    }

    setIsLoading(true);
    
    try {
      // Build the redirect URL - ensure it's absolute and correctly formatted
      const origin = window.location.origin;
      const redirectTo = `${origin}/callback`;
      
      console.log("Sending magic link with redirect URL:", redirectTo);
        
      // Send a magic link to the user's email
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
          // Make sure we're requesting a sign-in, not a password reset
          shouldCreateUser: false,
        }
      });
      
      if (error) throw error;
      
      console.log("Magic link response:", data);
      toast.success(t('magic_link_sent'));
      setCooldownRemaining(60); // 60 second cooldown
      
      // Show additional info for development
      if (process.env.NODE_ENV === 'development') {
        console.log("This is a development environment. In production, users would receive an email.");
        toast.info(t('for_testing_use'));
      }
    } catch (error: any) {
      console.error('Magic link error:', error);
      
      // Provide more specific error messages based on error type
      if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
        toast.error(t('reset_rate_limit_exceeded'));
        setCooldownRemaining(60);
      } else if (error.message.includes('delivery') || error.message.includes('email provider')) {
        toast.error(t('email_delivery_not_configured'));
        console.error('Email delivery issue. Check Supabase email provider configuration.');
      } else {
        toast.error(error.message || t('magic_link_error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const buttonDisabled = isLoading || cooldownRemaining > 0;
  
  return (
    <form onSubmit={handleRequestReset} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">{t('email_address')}</Label>
        <Input
          id="email"
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      <Button type="submit" className="w-full" disabled={buttonDisabled}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('processing')}
          </>
        ) : cooldownRemaining > 0 ? (
          t('wait_before_retry')
        ) : (
          t('send_magic_link')
        )}
      </Button>

      {cooldownRemaining > 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          {t('reset_rate_limit_message').replace('{{seconds}}', String(cooldownRemaining))}
        </p>
      )}

      {process.env.NODE_ENV === 'development' && (
        <p className="text-xs text-gray-500 mt-2">
          {t('for_testing_use')}
        </p>
      )}

      <Button
        type="button"
        variant="link"
        className="w-full"
        onClick={onBack}
      >
        {t('back')}
      </Button>
    </form>
  );
}
